/**
 * Service for activity interaction business logic (comments, reactions)
 */
import type { CommentResponseDto } from '@splitfiesta/shared';

import type { ReactionWithUser } from '@/types/api';

export interface ReactionSummary {
  emoji: string;
  count: number;
  hasReacted: boolean;
  users?: Array<{ id: string; name: string; addedAt: string }>;
}

export interface CommentValidationResult {
  isValid: boolean;
  error?: string;
  sanitizedText?: string;
}

export interface OptimisticReaction {
  userId: string;
  emoji: string;
  createdAt: string;
  userName?: string;
  isOptimistic: true;
}

export interface OptimisticComment {
  id: string;
  userId: string;
  name: string;
  profileImage?: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
  isOptimistic: true;
  replies: CommentResponseDto[];
}

class ActivityInteractionService {
  /**
   * Validate comment text
   */
  validateComment(text: string): CommentValidationResult {
    const trimmedText = text.trim();

    if (!trimmedText) {
      return {
        isValid: false,
        error: 'Comment cannot be empty',
      };
    }

    if (trimmedText.length > 500) {
      return {
        isValid: false,
        error: 'Comment must be less than 500 characters',
      };
    }

    // Basic sanitization (remove excessive whitespace)
    const sanitizedText = trimmedText.replace(/\s+/g, ' ');

    return {
      isValid: true,
      sanitizedText,
    };
  }

  /**
   * Format comment for display
   */
  formatComment(comment: CommentResponseDto | OptimisticComment): {
    displayText: string;
    isEdited: boolean;
    timeAgo: string;
  } {
    const now = new Date();
    const commentDate = new Date(comment.createdAt);

    return {
      displayText: comment.comment,
      isEdited:
        'updatedAt' in comment && comment.updatedAt !== comment.createdAt,
      timeAgo: this.getTimeAgo(commentDate, now),
    };
  }

  /**
   * Process reactions for display
   */
  processReactions(
    reactions: Array<ReactionWithUser | OptimisticReaction>,
    currentUserId?: string
  ): ReactionSummary[] {
    const reactionMap = new Map<string, ReactionSummary>();

    reactions.forEach(reaction => {
      const emoji = reaction.emoji;
      const userId = reaction.userId;
      const userName = 'userName' in reaction ? reaction.userName : 'User';

      if (!reactionMap.has(emoji)) {
        reactionMap.set(emoji, {
          emoji,
          count: 0,
          hasReacted: false,
          users: [],
        });
      }

      const summary = reactionMap.get(emoji)!;
      summary.count++;
      summary.users!.push({
        id: userId,
        name: userName || 'User',
        addedAt: reaction.createdAt,
      });

      if (currentUserId && userId === currentUserId) {
        summary.hasReacted = true;
      }
    });

    // Sort by count (most reactions first) then by emoji
    return Array.from(reactionMap.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.emoji.localeCompare(b.emoji);
    });
  }

  /**
   * Check if user can add reaction
   */
  canAddReaction(
    existingReactions: Array<ReactionWithUser | OptimisticReaction>,
    userId: string,
    emoji: string
  ): boolean {
    // Check if user already reacted with this emoji
    const userReaction = existingReactions.find(
      r => r.userId === userId && r.emoji === emoji
    );

    return !userReaction;
  }

  /**
   * Create optimistic reaction
   */
  createOptimisticReaction(
    emoji: string,
    user: { id: string; name: string }
  ): OptimisticReaction {
    return {
      userId: user.id,
      userName: user.name,
      emoji,
      createdAt: new Date().toISOString(),
      isOptimistic: true,
    };
  }

  /**
   * Create optimistic comment
   */
  createOptimisticComment(
    text: string,
    user: { id: string; name: string; profileImage?: string }
  ): OptimisticComment {
    const now = new Date().toISOString();
    return {
      id: `optimistic-comment-${Date.now()}`,
      userId: user.id,
      name: user.name,
      profileImage: user.profileImage,
      comment: text,
      createdAt: now,
      updatedAt: now,
      replies: [],
      isOptimistic: true,
    };
  }

  /**
   * Merge optimistic reactions with server data
   */
  mergeOptimisticReactions(
    serverReactions: ReactionWithUser[],
    optimisticReactions: OptimisticReaction[]
  ): Array<ReactionWithUser | OptimisticReaction> {
    // Filter out optimistic reactions that might have been confirmed
    const stillPendingOptimistic = optimisticReactions.filter(
      opt => !this.isReactionConfirmed(opt, serverReactions)
    );

    return [...serverReactions, ...stillPendingOptimistic];
  }

  /**
   * Check if optimistic reaction has been confirmed by server
   */
  private isReactionConfirmed(
    optimistic: OptimisticReaction,
    serverReactions: ReactionWithUser[]
  ): boolean {
    // Check if there's a matching server reaction (same user, same emoji)
    // that was created around the same time
    const matchingReaction = serverReactions.find(
      sr =>
        sr.userId === optimistic.userId &&
        sr.emoji === optimistic.emoji &&
        Math.abs(
          new Date(sr.createdAt).getTime() -
            new Date(optimistic.createdAt).getTime()
        ) < 5000 // Within 5 seconds
    );

    return !!matchingReaction;
  }

  /**
   * Calculate comment statistics
   */
  calculateCommentStats(comments: CommentResponseDto[]) {
    const stats = {
      totalComments: comments.length,
      uniqueCommenters: new Set<string>(),
      averageLength: 0,
      editedCount: 0,
    };

    let totalLength = 0;

    comments.forEach(comment => {
      stats.uniqueCommenters.add(comment.userId);
      totalLength += comment.comment.length;

      if (comment.updatedAt && comment.updatedAt !== comment.createdAt) {
        stats.editedCount++;
      }
    });

    stats.averageLength =
      comments.length > 0 ? Math.round(totalLength / comments.length) : 0;

    return {
      ...stats,
      uniqueCommenters: stats.uniqueCommenters.size,
    };
  }

  /**
   * Group comments by user for threading
   */
  groupCommentsByUser(
    comments: CommentResponseDto[]
  ): Map<string, CommentResponseDto[]> {
    const grouped = new Map<string, CommentResponseDto[]>();

    comments.forEach(comment => {
      const userId = comment.userId;

      if (!grouped.has(userId)) {
        grouped.set(userId, []);
      }

      grouped.get(userId)!.push(comment);
    });

    return grouped;
  }

  /**
   * Get popular reactions (most used emojis)
   */
  getPopularReactions(
    reactions: ReactionWithUser[],
    limit = 5
  ): Array<{ emoji: string; count: number }> {
    const emojiCounts = new Map<string, number>();

    reactions.forEach(reaction => {
      const count = emojiCounts.get(reaction.emoji) || 0;
      emojiCounts.set(reaction.emoji, count + 1);
    });

    return Array.from(emojiCounts.entries())
      .map(([emoji, count]) => ({ emoji, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Check if comment can be edited
   */
  canEditComment(
    comment: CommentResponseDto,
    currentUserId: string,
    maxEditTime = 15 * 60 * 1000 // 15 minutes
  ): boolean {
    // Only comment author can edit
    if (comment.userId !== currentUserId) return false;

    // Check if within edit time window
    const commentAge = Date.now() - new Date(comment.createdAt).getTime();
    return commentAge < maxEditTime;
  }

  /**
   * Check if comment can be deleted
   */
  canDeleteComment(
    comment: CommentResponseDto,
    currentUserId: string,
    isGroupOwner = false
  ): boolean {
    // Author can always delete their own comments
    if (comment.userId === currentUserId) return true;

    // Group owner can delete any comment
    return isGroupOwner;
  }

  /**
   * Format time ago string
   */
  private getTimeAgo(date: Date, now: Date): string {
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

    return date.toLocaleDateString();
  }

  /**
   * Sanitize user input for display
   */
  sanitizeUserInput(text: string): string {
    // Remove any HTML tags
    const withoutHtml = text.replace(/<[^>]*>/g, '');

    // Replace multiple spaces with single space
    const normalized = withoutHtml.replace(/\s+/g, ' ');

    // Trim whitespace
    return normalized.trim();
  }

  /**
   * Generate mention suggestions from comments
   */
  getMentionSuggestions(
    comments: CommentResponseDto[],
    currentUserId: string
  ): Array<{ id: string; name: string }> {
    const users = new Map<string, string>();

    comments.forEach(comment => {
      if (comment.userId !== currentUserId && comment.name) {
        users.set(comment.userId, comment.name);
      }
    });

    return Array.from(users.entries()).map(([id, name]) => ({ id, name }));
  }

  /**
   * Check if text contains mentions
   */
  extractMentions(text: string): string[] {
    const mentionPattern = /@(\w+)/g;
    const mentions: string[] = [];
    let match;

    while ((match = mentionPattern.exec(text)) !== null) {
      mentions.push(match[1]);
    }

    return mentions;
  }

  /**
   * Create reaction tooltip text
   */
  createReactionTooltip(summary: ReactionSummary, maxNames = 3): string {
    const users = summary.users || [];
    const displayNames = users
      .slice(0, maxNames)
      .map(u => u.name)
      .join(', ');

    const remainingCount = users.length - maxNames;

    if (remainingCount > 0) {
      return `${displayNames} and ${remainingCount} other${
        remainingCount > 1 ? 's' : ''
      }`;
    }

    return displayNames;
  }
}

export const activityInteractionService = new ActivityInteractionService();
